import { AnalyticsSelectedProductType } from "@intelligems/ig-types";
import { cleanParse } from "@intelligems/ig-utils";
import { fromGqlId } from "@src/api/formatters/shopify";

import {
  IExperienceDashboardFilters,
  IExperienceDashboardFiltersSchema,
} from "../types/dashboardFilters";

const toExclude: Set<keyof IExperienceDashboardFilters> = new Set([
  "hiddenEndTime",
  "experienceId",
]);

export const SYNCABLE_FILTERS: (keyof IExperienceDashboardFilters)[] = (
  Object.keys(
    IExperienceDashboardFiltersSchema.shape
  ) as (keyof IExperienceDashboardFilters)[]
).filter((i) => !toExclude.has(i));

const NUMERIC_FILTERS: (keyof IExperienceDashboardFilters)[] = [
  "startTime",
  "endTime",
  "hiddenEndTime",
  "shippingExpensePerOrder",
  "transactionFeePct",
  "maxZScore",
];

const serializeSelectedAnalyticsProducts = (
  products: AnalyticsSelectedProductType[] | null | undefined
): string | null => {
  if (!Array.isArray(products) || products.length === 0) {
    return null;
  }
  const ids = products
    .map((product) => fromGqlId(product.id))
    .filter((id) => id.trim() !== "");
  return ids.length > 0 ? ids.join(",") : null;
};

const deserializeSelectedAnalyticsProducts = (
  value: string
): AnalyticsSelectedProductType[] => {
  const trimmed = value.trim();

  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .filter(
          (product): product is { id: string | number } =>
            product != null &&
            typeof product === "object" &&
            "id" in product &&
            product.id != null
        )
        .map((product) => ({
          id: fromGqlId(String(product.id)),
          title:
            "title" in product && typeof product.title === "string"
              ? product.title
              : null,
          handle:
            "handle" in product && typeof product.handle === "string"
              ? product.handle
              : "",
        }));
    } catch {
      return [];
    }
  }

  return trimmed
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id !== "")
    .map((id) => ({
      id,
      title: null,
      handle: "",
    }));
};

export const serializeFilterValue = (
  value: any,
  key?: keyof IExperienceDashboardFilters
): string | null => {
  if (key === "selectedAnalyticsProducts") {
    return serializeSelectedAnalyticsProducts(value);
  }
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

export const deserializeValue = (
  value: string,
  key: keyof IExperienceDashboardFilters
): any => {
  if (key === "selectedAnalyticsProducts") {
    return deserializeSelectedAnalyticsProducts(value);
  }

  if (value === "null") return null;
  if (value === "undefined") return undefined;
  if (value === "true") return true;
  if (value === "false") return false;

  if (value.startsWith("[") || value.startsWith("{")) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  if (NUMERIC_FILTERS.includes(key)) {
    const num = Number(value);
    return isNaN(num) ? value : num;
  }

  return value;
};

export const getFiltersFromUrl = (): Partial<IExperienceDashboardFilters> => {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const filters: Partial<IExperienceDashboardFilters> = {};

  SYNCABLE_FILTERS.forEach((key) => {
    if (params.has(key)) {
      const value = params.get(key);
      if (value !== null) {
        filters[key] = deserializeValue(value, key);
      }
    }
  });

  // "subpage" parameter maps to "pageName"
  if (params.has("subpage") && !params.has("pageName")) {
    const subpageValue = params.get("subpage");
    if (subpageValue !== null) {
      filters.pageName = deserializeValue(subpageValue, "pageName");
    }
  }
  return cleanParse(IExperienceDashboardFiltersSchema, filters);
};
